// src/context/AppContext.js
//
// Estado global de la app: empresa activa, sucursales de esa empresa,
// sucursal en operación (o "todas"), snackbar.
//
// Cómo se resuelve la empresa según el usuario que entra:
//
//   Rol NEGOCIO (tendero):
//     El backend (BR2) devuelve empresaId en el LoginResponse.
//     → GET /api/pos/empresas/{empresaId}
//     → GET /api/pos/sucursales?empresaId=X (todas las sucursales de esa empresa)
//     Se hace automático. El tendero no elige empresa.
//
//   Rol SUPERADMIN:
//     No hay empresa asociada (entidadId=0, sin empresaId).
//     → GET /api/pos/empresas para listar todas
//     → El usuario elige cuál ver (dialog).
//     → Se guarda en localStorage 'pos_empresa_id'.
//
// Sucursal activa:
//   - null → Modo "Todas las sucursales" (dashboard consolidado, sin POS/Caja).
//   - <SucursalId> → Modo "Sucursal específica" (operativo completo).
//   - Si la empresa tiene 1 sola sucursal, se auto-selecciona y no se
//     muestra la opción "Todas".

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { empresas as apiEmpresas, sucursales as apiSucursales } from '../api/posApi';
import { mockNegocios } from '../mock/data';

const AppContext = createContext(null);
const STORAGE_EMPRESA_ID  = 'pos_empresa_id';
const STORAGE_SUCURSAL_ID = 'pos_sucursal_id';

// Fallback SOLO para las páginas del POS aún no migradas al backend real
// (Dashboard, POS, Inventario, Clientes, Ventas, Caja, etc.). Devuelve
// el mockNegocios[0] mientras `empresa` real es null, para que esas
// páginas no exploten al leer campos como `negocio.facturacionElectronica`
// o `negocio.modoOperacion`.
//
// A medida que las páginas se migren (C4-C10), dejarán de usar `negocio`
// y este fallback se puede eliminar.
const NEGOCIO_FALLBACK_MOCK = mockNegocios[0];

export const AppProvider = ({ children }) => {
  const { user } = useAuth();

  const [empresa, setEmpresa]                    = useState(null);
  const [sucursales, setSucursales]              = useState([]);     // todas las sucursales de la empresa
  const [sucursalActivaId, setSucursalActivaId]  = useState(null);   // null = "Todas"
  const [empresasDisponibles, setEmpresasDisp]   = useState(null);   // solo SUPERADMIN
  const [cargando, setCargando]                  = useState(false);
  const [errorCarga, setErrorCarga]              = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  /** Carga sucursales de la empresa y decide sucursal activa. */
  const cargarSucursales = useCallback(async (empresaId) => {
    try {
      const lista = await apiSucursales.listar(empresaId);
      const items = Array.isArray(lista) ? lista : (lista.items || []);
      setSucursales(items);

      // Regla de auto-selección:
      // - 0 sucursales: activa=null (no hay nada que operar).
      // - 1 sucursal: activa = esa (no tiene sentido "Todas").
      // - 2+: si hay una previa guardada válida, se restaura; si no, activa=null (Todas).
      if (items.length === 0) {
        setSucursalActivaId(null);
      } else if (items.length === 1) {
        setSucursalActivaId(items[0].sucursalId || items[0].SucursalId);
      } else {
        const savedId = localStorage.getItem(STORAGE_SUCURSAL_ID);
        const parsed  = savedId ? parseInt(savedId, 10) : null;
        const existe  = parsed && items.some(s => (s.sucursalId || s.SucursalId) === parsed);
        setSucursalActivaId(existe ? parsed : null);
      }
    } catch {
      setSucursales([]);
      setSucursalActivaId(null);
    }
  }, []);

  const cargarEmpresaDelUsuario = useCallback(async () => {
    if (!user) {
      setEmpresa(null);
      setSucursales([]);
      setSucursalActivaId(null);
      setEmpresasDisp(null);
      return;
    }

    setCargando(true);
    setErrorCarga('');
    try {
      if (user.entidadTipo === 'NEGOCIO') {
        // BR2: el JWT trae empresaId directo. No hay que resolverlo.
        const empresaId = user.empresaId;
        if (!empresaId) {
          throw new Error('El usuario NEGOCIO no trae empresa_id en el JWT. Redespliega el backend con BR2.');
        }
        console.log('[AppContext] NEGOCIO detectado, empresaId del JWT =', empresaId);
        const emp = await apiEmpresas.obtener(empresaId);
        setEmpresa(emp);
        await cargarSucursales(empresaId);
      } else if (user.entidadTipo === 'SUPERADMIN') {
        console.log('[AppContext] SUPERADMIN detectado, listando empresas');
        const lista = await apiEmpresas.listar();
        setEmpresasDisp(Array.isArray(lista) ? lista : (lista.items || []));

        const savedId = localStorage.getItem(STORAGE_EMPRESA_ID);
        if (savedId) {
          try {
            const emp = await apiEmpresas.obtener(parseInt(savedId, 10));
            setEmpresa(emp);
            await cargarSucursales(emp.empresaId || emp.EmpresaId);
          } catch { localStorage.removeItem(STORAGE_EMPRESA_ID); }
        }
      }
    } catch (e) {
      const msg = e.mensajeUsuario || e.message || 'No se pudo cargar la empresa.';
      console.error('[AppContext] Error cargando empresa:', e);
      setErrorCarga(msg);
    } finally {
      setCargando(false);
    }
  }, [user, cargarSucursales]);

  useEffect(() => {
    cargarEmpresaDelUsuario();
  }, [cargarEmpresaDelUsuario]);

  // ─── SUPERADMIN elige empresa ───
  const seleccionarEmpresa = useCallback(async (empresaId) => {
    if (!empresaId) return;
    setCargando(true);
    try {
      const emp = await apiEmpresas.obtener(empresaId);
      setEmpresa(emp);
      localStorage.setItem(STORAGE_EMPRESA_ID, String(empresaId));
      await cargarSucursales(empresaId);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo cargar la empresa.', 'error');
    } finally {
      setCargando(false);
    }
  }, [notify, cargarSucursales]);

  const cambiarEmpresa = useCallback(() => {
    setEmpresa(null);
    setSucursales([]);
    setSucursalActivaId(null);
    localStorage.removeItem(STORAGE_EMPRESA_ID);
    localStorage.removeItem(STORAGE_SUCURSAL_ID);
  }, []);

  // ─── Selección de sucursal activa ───
  /**
   * Cambia la sucursal en operación.
   * @param {number|null} id  SucursalId o null para "Todas las sucursales".
   */
  const seleccionarSucursal = useCallback((id) => {
    setSucursalActivaId(id);
    if (id === null) localStorage.removeItem(STORAGE_SUCURSAL_ID);
    else             localStorage.setItem(STORAGE_SUCURSAL_ID, String(id));
  }, []);

  const refrescarEmpresa = useCallback(async () => {
    if (!empresa) return;
    const id = empresa.empresaId || empresa.EmpresaId;
    if (!id) return;
    try {
      const fresh = await apiEmpresas.obtener(id);
      setEmpresa(fresh);
      await cargarSucursales(id);
    } catch { /* ignore */ }
  }, [empresa, cargarSucursales]);

  // ─── Derivados útiles ───
  const sucursalActiva = sucursales.find(
    s => (s.sucursalId || s.SucursalId) === sucursalActivaId
  ) || null;
  const esModoTodas = sucursalActivaId === null && sucursales.length > 1;
  const esModoSucursal = sucursalActivaId !== null;
  const hayVariasSucursales = sucursales.length > 1;

  return (
    <AppContext.Provider value={{
      empresa, sucursales, sucursalActiva, sucursalActivaId,
      esModoTodas, esModoSucursal, hayVariasSucursales,
      empresasDisponibles,
      seleccionarEmpresa, cambiarEmpresa, refrescarEmpresa,
      seleccionarSucursal,
      cargando, errorCarga,
      reintentarCargaEmpresa: cargarEmpresaDelUsuario,
      notify, snackbar, closeSnackbar,

      // Compatibilidad con páginas mock aún no migradas (C4-C10):
      // devolvemos la empresa real cuando ya cargó, o el mock como
      // fallback para que campos como facturacionElectronica y
      // modoOperacion no exploten mientras la migración avanza.
      negocio: empresa || NEGOCIO_FALLBACK_MOCK,
      updateNegocio: () => notify('Usa el módulo Configuración para editar los datos.', 'info'),
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
